import {
  NodeKind,
  effectiveRatio,
  findNode,
  parentNode,
  type CompositionNode,
  type FrameNode,
  type ImageNode,
} from '@/core/document'
import type { SourceImage } from '@/core/image'
import { estimateExportBytes, planExport, type ExportOptions } from '@/core/export'
import type { EditSessionState } from '@/core/store/editSession'

interface ExportEstimateState {
  document: EditSessionState['document']
  sources: EditSessionState['sources']
  exportOptions: ExportOptions
}

export interface ExportEstimates {
  outputs: readonly number[]
  total: number
}

export function selectCanUndo(state: EditSessionState): boolean {
  return state.history.past.length > 0
}

export function selectCanRedo(state: EditSessionState): boolean {
  return state.history.future.length > 0
}

export function selectRootNode(state: EditSessionState): CompositionNode {
  if (!state.document) throw new Error('Root node is unavailable because no document is loaded')
  return state.document.root
}

export function selectSelectedNode(state: EditSessionState): ImageNode {
  if (!state.document) throw new Error('Selected node is unavailable because no document is loaded')
  if (!state.selectedNodeId) throw new Error('No active photo is available in the document')
  const node = findNode(state.document.root, state.selectedNodeId)
  if (!node) throw new Error(`Selected node "${state.selectedNodeId}" is missing from the document`)
  if (node.kind !== NodeKind.Image) throw new Error(`Active photo must be an Image node, received "${node.kind}"`)
  return node
}

export function selectParentFrame(state: EditSessionState): FrameNode | null {
  if (!state.document) throw new Error('Parent frame is unavailable because no document is loaded')
  const selected = selectSelectedNode(state)
  const parent = parentNode(state.document.root, selected.id)
  return parent?.kind === NodeKind.Frame ? parent : null
}

export function selectSelectedSource(state: EditSessionState): SourceImage {
  const node = selectSelectedNode(state)
  if (node.kind !== NodeKind.Image) {
    throw new Error(`Selected node "${node.id}" is not an image node`)
  }
  const source = state.sources.get(node.sourceId)
  if (!source) throw new Error(`Selected image node "${node.id}" references missing source "${node.sourceId}"`)
  return source
}

export function selectEffectiveRatio(state: EditSessionState) {
  if (!state.document) throw new Error('Effective ratio is unavailable because no document is loaded')
  if (!state.selectedNodeId) throw new Error('Effective ratio is unavailable because no active photo exists')
  return effectiveRatio(state.document, state.sources, state.selectedNodeId)
}

export function selectExportEstimates(state: ExportEstimateState): ExportEstimates {
  if (!state.document) throw new Error('Export estimates are unavailable because no document is loaded')
  const plan = planExport(state.document, state.sources, state.exportOptions)
  if (plan.outputs.length === 0) throw new Error('Export estimate plan did not produce an output')
  const outputs = plan.outputs.map((output) => estimateExportBytes(output.targetSize, state.exportOptions))
  return { outputs, total: outputs.reduce((total, bytes) => total + bytes, 0) }
}
